import React from 'react';
import PropTypes from 'prop-types';
import { Transition } from "react-transition-group";
import ArrowRight from "../ArrowRight";
import styled, { css } from "styled-components";
import { getColor, StyleVars, Fonts, typeScale } from "core/utils";
import { Content, SectionSubtitle } from "core/styles";

const Slide = ({ slide, idx, duration, index }) => (
	<Transition in={idx === index} timeout={duration}>
		{status => (
			<>
				<BigSlideWrapper
					key={idx}
					active={idx === index}
					status={status}
					duration={duration}
				>
					<ContentContainer>
						<CollectionContainer
							color={slide.accentColor}
							lightTheme={slide.lightTheme}
						>
							{slide.subTitle && (
								<SlideSmallTitle>
									{slide.subTitle}
								</SlideSmallTitle>
							)}
							<SlideActionHeader>
								{slide.title}
							</SlideActionHeader>
							<SlideContent>
								{slide.description}
							</SlideContent>

							<LinksWrapper>
								{slide.cta && slide.link && (
									<CtaBtn href={slide.link} title={slide.cta}>
										{slide.cta} <ArrowRight />
									</CtaBtn>
								)}

								{slide.secondaryCta && slide.secondaryLink && (
									<CtaBtn href={slide.secondaryLink} title={slide.secondaryCta} $secondary>
										{slide.secondaryCta} <ArrowRight />
									</CtaBtn>
								)}
							</LinksWrapper>
						</CollectionContainer>
					</ContentContainer>
					<ContentContainer>
						<HeroImage
							key={idx}
							data-src={slide.image}
							alt={
								`${slide.title} Photo ${index + 1}`
							}
							title={
								`${slide.title} Photo ${index + 1}`
							}
							className="lazyload"
						/>
					</ContentContainer>
				</BigSlideWrapper>
			</>
		)}
	</Transition>
);

Slide.propTypes = {
	idx: PropTypes.number,
	index: PropTypes.number,
	duration: PropTypes.number,
	slide: PropTypes.object,
};

Slide.defaultProps = {
	duration: 750,
};

const checkStatus = status => {
	if (status === "entering" || status === "entered") {
		return css`opacity: 1;`;
	}

	if (status === "exited" || status === "exiting") {
		return css`opacity: 0;`;
	}
};

const BigSlideWrapper = styled.div`
	display: flex;
	flex-direction: row;
	visibility: ${props => (props.active ? "visible" : "hidden")};
	overflow: hidden;
	opacity: 1;
	transition: ${({ duration }) => duration || 750 }ms ease-in-out;
	width: 100%;

	${({ status }) => checkStatus(status)}

	${props =>
		!props.active &&
		css`
			top: 0;
			left: 0;
			position: absolute;
		`}

	@media (max-width: ${StyleVars.screenSm}) {
		flex-flow: column-reverse;
		padding: 0;
	}
`;

const SlideActionHeader = styled.h1`
	margin: ${StyleVars.defaultSpacingUnit / 2}px 0;
	font-style: italic;
	font-family: ${Fonts.serif};
	font-weight: 400;
	margin-bottom: ${typeScale(3)};
`;

const CollectionContainer = styled.div`
	padding: 5em;
	background-color: ${({ color }) => color ? color.toLowerCase() : getColor("salmon")};
	color: ${({ lightTheme }) => lightTheme ? getColor("gray") : getColor("white")};
	height: inherit;
	display: flex;
	flex-direction: column;
	justify-content: start;

	@media (max-width: ${StyleVars.screenSm}) {
		padding: 1em;
	}
`;

const ContentContainer = styled.div`
	width: 100%;
	height: 450px;

	/*
	* 1160px x 450px requirment comes from design
	*/
	@media (min-width: 1160px) {
		width: 50%;
	}

	@media (max-width: ${StyleVars.screenSm}) {
		height: 285px;
	}

	@media (max-width ${StyleVars.screenXsm}) {
		height: 463px;
	}
`;

const HeroImage = styled.img`
	height: 100%;
	width: 100%;
	object-fit: cover;
`;

const SlideContent = styled(Content)`
	color: inherit;
	font-family: ${Fonts.serif};
	font-size: ${typeScale(2.5)};
`;

const CtaBtn = styled.a`
	/* font-size by designer */
	font-size: ${typeScale(2.25)};
	display: block;
	margin-top: ${typeScale(1)};
	border: 1px solid;
	border-radius: 8px;
	padding: ${typeScale(1.5)} ${typeScale(2)};

	background: ${getColor("white")};
	color: ${getColor("black")};
	border-color: ${getColor("white")};

	&:hover {
		text-decoration: underline;
	}

	${({ $secondary }) => $secondary && css`
		background: transparent;
		color: ${getColor("white")};
		border-color: ${getColor("white")};
	`}

	> svg {
		fill: inherit;
		margin-left: ${typeScale(0.5)};
	}
`;

const SlideSmallTitle = styled(SectionSubtitle)`
	font-weight: 300;
`;

const LinksWrapper = styled.div`
	padding: ${typeScale(1)} 0;
	@media (min-width: 768px) {
		display: flex;
		align-items: center;
		gap: ${typeScale(1)};
	}
`;

export default Slide;
